(() => {
  if (customElements.get('lfd-product-bestsellers')) return;

  const rules = window.LFDBestsellerRules;
  if (!rules) return;

  class ProductBestsellers extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.controller = new AbortController();
      this.container = this.closest('.shopify-section');
      delete this.dataset.ready;
      delete this.dataset.empty;
      delete this.dataset.carouselInitialized;
      try {
        this.config = JSON.parse(this.querySelector('[data-lfd-bestseller-config]').textContent);
        this.limit = Math.min(10, Math.max(1, Number(this.dataset.limit) || 8));
      } catch {
        this.finishEmpty();
        return;
      }
      this.setAttribute('aria-busy', 'true');
      if (this.container) this.container.hidden = false;
      this.load();
    }

    disconnectedCallback() {
      this.layoutObserver?.disconnect();
      cancelAnimationFrame(this.layoutFrame);
      this.controller?.abort();
      this.container?.lfdPopularResizeObserver?.disconnect();
      this.querySelector('.product-carousel-swiper')?.swiper?.destroy();
      this.modals?.forEach(modal => modal.remove());
      this.started = false;
    }

    endpoint(path) {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin) throw new Error('Unexpected product origin');
      url.searchParams.set('section_id', this.dataset.section);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url;
    }

    async request(url) {
      const controller = new AbortController(), lifetime = this.controller;
      const cancel = () => controller.abort();
      lifetime.signal.addEventListener('abort', cancel, { once: true });
      if (lifetime.signal.aborted) controller.abort();
      const timer = setTimeout(cancel, 12000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Bestseller request failed');
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      } finally {
        clearTimeout(timer);
        lifetime.signal.removeEventListener('abort', cancel);
      }
    }

    async card(item, catalog) {
      try {
        const url = this.endpoint(item.url);
        url.searchParams.set('view', 'lfd-bestseller-card');
        const doc = await this.request(url);
        const card = doc.querySelector('[data-lfd-bestseller-id]');
        return this.validCard(card, item, catalog) ? card : null;
      } catch {
        return null;
      }
    }

    validCard(card, item, catalog) {
      try {
        const current = JSON.parse(card?.querySelector('[data-lfd-bestseller-item]')?.textContent || 'null');
        return current && String(current.id) === String(item.id) && rules.matches(this.config.subject, current, catalog) && rules.category(current) === rules.category(item);
      } catch { return false; }
    }

    async cards(items, catalog) {
      const lifetime = this.controller;
      try {
        const url = this.endpoint(this.config.search || `${this.config.root.replace(/\/$/, '')}/search`);
        const handles = items.map(item => decodeURIComponent(new URL(item.url, location.origin).pathname.split('/').filter(Boolean).at(-1)));
        url.searchParams.set('view', 'lfd-bestseller-cards');
        url.searchParams.set('type', 'product');
        url.searchParams.set('q', handles.join('|'));
        const doc = await this.request(url);
        if (!doc.querySelector('[data-lfd-bestseller-batch]')) throw new Error('Batch unavailable');
        const received = new Map(Array.from(doc.querySelectorAll('[data-lfd-bestseller-id]'), card => [card.dataset.lfdBestsellerId, card]));
        return items.map(item => {
          const card = received.get(String(item.id));
          return this.validCard(card, item, catalog) ? card : null;
        });
      } catch {
        if (lifetime.signal.aborted || lifetime !== this.controller) return items.map(() => null);
        return Promise.all(items.map(item => this.card(item, catalog)));
      }
    }

    arrangeCard(card) {
      const group = card.querySelector('.card-information__group');
      const note = card.querySelector('[data-lfd-bestseller-note]');
      if (group && note) group.append(note);
      card.classList.add('lfd-bestseller-aligned');
    }

    alignRows() {
      const schedule = () => {
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(() => {
          if (!this.isConnected || !this.dataset.ready) return;
          const names = ['heading', 'stock', 'purchase'];
          names.forEach(name => this.style.setProperty(`--lfd-${name}-height`, '0px'));
          const heights = names.map(name => Math.ceil(Math.max(0, ...Array.from(this.querySelectorAll(`.lfd-bestseller-${name}-row`), row => row.getBoundingClientRect().height))));
          names.forEach((name, i) => this.style.setProperty(`--lfd-${name}-height`, `${heights[i]}px`));
        });
      };
      this.layoutObserver?.disconnect();
      const widths = new WeakMap();
      this.layoutObserver = new ResizeObserver(entries => {
        if (entries.some(entry => {
          const changed = widths.get(entry.target) !== entry.contentRect.width;
          widths.set(entry.target, entry.contentRect.width);
          return changed;
        })) schedule();
      });
      this.layoutObserver.observe(this);
      const firstCard = this.querySelector('.lfd-bestseller-aligned');
      if (firstCard) this.layoutObserver.observe(firstCard);
      document.fonts?.ready.then(schedule);
      schedule();
    }

    async load() {
      const lifetime = this.controller, ranked = [], seen = new Set();
      try {
        if (!rules.plan(this.config.subject).primary.length) {
          this.finishEmpty();
          return;
        }
        const catalogPage = async page => {
          const root = this.config.root.replace(/\/$/, '');
          const url = this.endpoint(`${root}/collections/all`);
          url.searchParams.set('sort_by', 'best-selling');
          url.searchParams.set('page', String(page));
          const doc = await this.request(url);
          const data = JSON.parse(doc.querySelector('[data-lfd-bestseller-page]')?.textContent || 'null');
          if (!data || data.sort !== 'best-selling' || data.page !== page || !Array.isArray(data.products)) throw new Error('Invalid bestseller order');
          return data;
        };
        const firstPage = await catalogPage(1);
        const pages = Math.max(1, Number(firstPage.pages) || 1), catalogPages = [firstPage];
        for (let page = 2; page <= pages && !lifetime.signal.aborted; page += 4) {
          catalogPages.push(...await Promise.all(Array.from({ length: Math.min(4, pages - page + 1) }, (_, i) => catalogPage(page + i))));
        }
        for (const data of catalogPages) {
          for (const item of data.products) {
            const id = String(item.id);
            if (seen.has(id)) continue;
            seen.add(id);
            ranked.push(item);
          }
        }
        const rendered = new Map(), rejected = new Set();
        let selected = [];
        while (!lifetime.signal.aborted) {
          const candidates = ranked.filter(item => !rejected.has(String(item.id)));
          selected = rules.select(this.config.subject, candidates, this.limit, this.config.excluded || []);
          const pending = selected.filter(item => !rendered.has(String(item.id)));
          if (!pending.length) break;
          const cards = await this.cards(pending, candidates);
          cards.forEach((card, i) => {
            const id = String(pending[i].id);
            if (card) rendered.set(id, card);
            else rejected.add(id);
          });
        }
        if (!this.isConnected || lifetime.signal.aborted || lifetime !== this.controller) return;
        const cards = selected.map(item => rendered.get(String(item.id))).filter(Boolean);
        if (!cards.length) {
          this.finishEmpty();
          return;
        }
        const target = this.querySelector('[data-lfd-bestseller-cards]');
        const source = rules.details(this.config.subject), messages = this.config.messages || {};
        cards.forEach((card, index) => {
          card.style.zIndex = String(1000 - index);
          const item = rules.details(selected[index]), notes = [];
          if ((source.category === 'barrel' && item.category === 'flight') || (source.category === 'flight' && item.category === 'barrel')) notes.push(messages.separate_setup);
          if (item.category === 'flight' && item.requiresRing === true) notes.push(messages.ring_required);
          if (item.category === 'scoring') notes.push(messages.scoring_setup);
          const note = card.querySelector('[data-lfd-bestseller-note]');
          if (note && notes.filter(Boolean).length) {
            note.textContent = notes.filter(Boolean).join(' ');
            note.hidden = false;
          }
          this.arrangeCard(card);
          if (index < 4) card.querySelectorAll('.media img').forEach(img => { img.loading = 'eager'; img.fetchPriority = 'low'; });
        });
        this.modals = cards.flatMap(card => Array.from(card.querySelectorAll('quick-add-modal')));
        target.replaceChildren(...cards);
        window.initButtonsAnimation?.(this);
        this.dataset.ready = 'true';
        this.setAttribute('aria-busy', 'false');
        this.querySelector('[data-lfd-bestseller-loading]')?.remove();
        this.querySelector('.popular-products').hidden = false;
        if (this.container) {
          this.container.hidden = false;
          this.container.dispatchEvent(new CustomEvent('lfd:popular-products:loaded', { bubbles: true }));
        }
        this.alignRows();
        window.Shopify?.PaymentButton?.init();
      } catch {
        if (this.isConnected && !lifetime.signal.aborted && lifetime === this.controller) this.finishEmpty();
      }
    }

    finishEmpty() {
      this.dataset.empty = 'true';
      this.setAttribute('aria-busy', 'false');
      if (this.container) this.container.hidden = true;
    }
  }

  customElements.define('lfd-product-bestsellers', ProductBestsellers);
})();
